/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo } from "react";
import { css, useTheme } from "@emotion/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Container,
  Icon,
  Text,
} from "@coreai/component-library";
import Markdown from "react-markdown";
import "./ResultSection.css";
import rehypeRaw from "rehype-raw";
import { useLanguage } from "LanguageContext";
import RateFeedback from "../../../CommonUi/RateFeedback/RateFeedback";
import AnalysisIcons from "../../../CommonUi/AnalysisIcons/AnalysisIcons";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import {TFeedbackDetails} from "../../../../services/feedbackService";
import { principleAnchorId } from "../../../../helpers/principleTokens";
import { useShowToggleDetails } from "hooks/useShowToggleDetails";

export interface SubPrinciples {
  sub_index: number;
  title: string;
  category: number;
  evaluation: string;
}
export interface AnalysisSummaryResult {
  title: string;
  evaluation?: string;
  feedbacks?: Array<TFeedbackDetails>;
  id?: string;
  index: number;
  content: string;
  category: number;
  sub_principles: Array<SubPrinciples>;
}
interface ResultSectionProps {
  title: string;
  items: AnalysisSummaryResult[];
  bgColor: string;
  icon: JSX.Element;
  openAll: boolean;
  showToggle: boolean;
}

export default function ResultSection(props: Readonly<ResultSectionProps>) {
  const { t, language } = useLanguage();
  const theme = useTheme();
  const { analysisContextResult } = useAnalysis();
  // const rgbColor = hexToRgba(props.bgColor);
  const [openReminders, setOpenReminders] = useState<Record<number, boolean>>({});
  const [openItems, setOpenItems] = useState<string[]>([]);
  const { shouldShow } = useShowToggleDetails(analysisContextResult);

  useEffect(() => {
    if (props.items) {
      const showList = props.openAll ? props.items.map((item) => item.title) : props.items.filter((item) => item.category === 1 || item.category === 2).map((item) => item.title);
      setOpenItems(showList);
    }
  }, [props.openAll, props.items]);

  const iconContainerStyles = css`
    background-color: ${props.bgColor};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  `;

  const accordionTriggerStyle = css`
    display: flex;
    justify-content: flex-start;
    align-items: end;
    text-align: left;
    padding-left: ${theme.spacing["5xl"]};
    font-family: ${theme.typography.heading4_medium.fontFamily};
    font-size: ${theme.typography.heading4_medium.fontSize};
    font-weight: ${theme.typography.heading4_medium.fontWeight};
  `;

  const titleHeader = css`
    margin-bottom: ${theme.spacing["xl"]};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing["xxl"]};
    white-space: nowrap;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  `;

  const titleLeftWrapper = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    @media (max-width: 768px) {
      align-self: flex-start;
    }
  `;

  const iconWrapper = css`
    cursor: pointer;
    margin-right: 28px;

    @media (max-width: 768px) {
      align-self: flex-end;
    }
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    color: ${theme.colors.textMedium};
    line-height: 120%;

     @media (max-width: 768px) {
      font-size: ${theme.typography.heading4_semiBold.fontSize};
    }
  `;

  const listStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    margin: 0 ${theme.spacing["xl"]} 0 0;
    margin-bottom: ${theme.spacing["xl"]};
  `;

  const itemStyles = (_cat: number) => css`
    background-color: ${theme.colors.white};
    font-family: ${theme.typography.heading4_medium.fontFamily};
    font-size: ${theme.typography.heading4_medium.fontSize};
    font-weight: ${theme.typography.heading4_medium.fontWeight};
    &:hover {
      background-color: ${theme.colors.white};
      cursor: default;
      transform: none;
    }
      position: relative;
      &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: calc(100% + 1px);
      background-color: ${_cat === -1 ? '#D3D7D9' : _cat === 0 ? '#F84241' : _cat === 1 ? '#15CB4A' : _cat === 3 ? '#A3CDED' : '#FFC400'};
    }
  `;

  const containerStyle = () => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing["4xl"]};
    text-align: left;
    border-radius: ${theme.radius.xs};

    @media (max-width: 768px) {
      padding: 12px;
    }
  `;

  const contentStyles = css`
    margin: 0 28px;
    padding-top: 0;
    p{
      margin-top: 0px;
    }
    .greyDot{
      background-color: #D3D7D9;
    }
    .greenDot{
      background-color: #15CB4A;
    }
    .yellowDot{
      background-color: #FFC400;
    }
    .blueDot{
      background-color: #A3CDED;
    }
    .redDot{
      background-color: #F84241;
    }
    .notAvilableDot{
      background-color: #FFC400;
    }
    .greyDot, .yellowDot, .blueDot, .redDot, .greenDot, .notAvilableDot {
      margin-right: 5px;
      border-radius: 50%;
    }
  `;

  const botStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
    gap: ${theme.spacing.md};
  `;

  const botContainerStyle = css`
    flex: 1;
    background-color: ${theme.colors.grey};
    color: ${theme.colors.primary};
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.body_semiBold.fontSize};
    border: 1px solid ${theme.colors.grey};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
    justify-content: flex-start;
  `;

  const resultSectionReminderStyles = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    color: ${theme.colors.feedbackTitleColor};
  `;

  const emptyTextTitle = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    color: ${theme.colors.primary};
  `;

  const subItemStyles = css`
    margin-bottom: 12px;
  `;

  const subItemTitleStyles =css`
    display: flex;
    justify-item: center;
    align-items: center;
    gap: 5px;

    span {
    width: 18px;
    height: 18px;
    }
  `;

  const titleValueStyles =css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    color: ${theme.colors.primary};
  `;

  const subItemDescStyles =css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    color: ${theme.colors.primary};
    padding-left: 26px;
  `;

  const toggleReminder = (index: number) => {
    setOpenReminders((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSectionToggle = () => {
    if (openItems.length === props.items.length) {
      setOpenItems([]); // collapse all
    } else {
      setOpenItems(props.items.map((item) => item.title)); // expand all
    }
  };

  return (
    <Container variant="column" css={containerStyle}>
      <div css={titleHeader}>
        <div css={titleLeftWrapper}>
            {/* <div css={iconContainerStyles}></div> */}
             {props?.title.split(" ")[0].toLowerCase() && <AnalysisIcons show="all" imgName={props?.title} colorId={props.bgColor} /> }
            <Text css={titleStyles} style={{ marginTop: "0px" }}>
              {window?.referencesFramework?.[props?.title]?.[analysisContextResult?.locale_code]?.title ?? props?.title ?? ''}
            </Text>
        </div>
        <div css={iconWrapper} onClick={() => handleSectionToggle()}>
            {false ? (
                <Icon name="chevron-up" size="sm" />
            ) : (
                <Icon name="chevron-down" size="sm" />
            )}
        </div>
      </div>

      <div css={listStyles}>
        <Accordion type="multiple" style={{ paddingBottom: 0, padding: 0 }}          
          value={openItems}
          onValueChange={setOpenItems}
        >
          {props.items?.map((item, index) => {
            const isReminderOpen = openReminders[index] || false;
            return (
              <AccordionItem
                id={principleAnchorId(props.title, item.index)}
                value={item.title}
                key={`${item.title}-${index}`}
                css={() => itemStyles(item?.category)}
              >
                <AccordionTrigger id={props?.title} css={accordionTriggerStyle}>
                  {window?.referencesFramework?.[props?.title]?.[analysisContextResult?.locale_code]?.subDomains?.[item?.title]?.title ?? item?.title ?? ''}
                </AccordionTrigger>
                <AccordionContent css={contentStyles}>
                  <div>
                  {item?.sub_principles?.map((_sub, idx) => (
                    <div css={subItemStyles} id={String(idx)} key={idx}>
                      <div css={subItemTitleStyles}>
                        <span className={_sub?.category === -1 ? 'greyDot' : _sub?.category === 0 ? 'redDot' : _sub?.category === 1 ? 'greenDot' : _sub?.category === 3 ? 'blueDot' : 'yellowDot'}></span>
                        <p css={titleValueStyles}>{_sub?.title}</p>
                      </div>
                      {(
                        (props?.showToggle) ||
                        (!props?.showToggle && shouldShow(_sub?.category))
                      )  
                        && <div css={subItemDescStyles}>{_sub?.evaluation}</div>
                      }
                    </div>
                  ))}
                  </div>
                  <div css={botStyle}>
                    <div
                      css={botContainerStyle}
                      onClick={() => toggleReminder(index)}
                    >
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {isReminderOpen ? (
                          <Icon name="minus" size="sm" />
                        ) : (
                          <Icon name="plus" size="sm" />
                        )}
                      </div>
                      <div css={resultSectionReminderStyles}>
                        {t("analyse_result_section_reminder", null, analysisContextResult?.locale_code)}
                      </div>
                    </div>
                  </div>
                  {isReminderOpen ?  
                    <Markdown rehypePlugins={[rehypeRaw]}>{
                      window?.referencesFramework?.[props?.title]?.[analysisContextResult?.locale_code]?.subDomains[item?.title]?.criteria?.length > 0 ?
                        window.referencesFramework[props.title][analysisContextResult.locale_code].subDomains[item.title].criteria
                          ?.map((val: any) => `- **${val?.title?.trim()}** <br /> ${val?.details}`)
                          ?.join('\n')
                        : item?.content.replace(/\\n/g, '<br />') ?? ''
                      }
                  </Markdown>
                    : null}
                  {item?.id && <RateFeedback title={t("feedback_buttonText", null, analysisContextResult?.locale_code)} feedbackPosition="Analysis" id={item?.id!} entityId={item?.id} entityType="response_id" feedbackDetails={item?.feedbacks?.[0]}/>}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>
      {props.items?.length === 0 && <div css={emptyTextTitle}>{t("positiveRep_no_principle")}</div>}
    </Container>
  );
}
